/**
 * Number of lines text will occupy when rendered, measured by the rendering
 * engine itself.
 *
 * The grid layout reserves every header height before a node exists: its count
 * must match **exactly** what the browser renders, or thumbnails fall beneath
 * text or a gap appears under the header. Estimating from text length and average
 * glyph width misses that precision, with no way to recover from underestimating
 * (D85).
 *
 * Hence an off-screen probe with **the same classes** and **the same width** as
 * the real paragraph: same font, wrapping, `leading` and `text-rendering`.
 * Measurement is correct by construction rather than calibration — unlike
 * `canvas.measureText`, whose metrics ignore the page body's `optimizeLegibility`.
 */

/**
 * One probe for all measurements, never removed: creating and inserting it on
 * every call would cost one style recalculation per annotated day.
 */
let probe: HTMLParagraphElement | null = null;

/**
 * Completed measurements indexed by text. Clear the cache whenever width changes
 * — all notes in one pass share it — bounding it to the number of annotated days
 * without eviction.
 */
const cache = new Map<string, number>();
let cacheWidth = -1;

function probeElement(className: string, width: number): HTMLParagraphElement {
  if (!probe) {
    probe = document.createElement('p');
    // `fixed` and outside the viewport: the probe participates in no flow, paints
    // nothing and does not lengthen the page. `visibility: hidden` alone would
    // leave it occupying space in the body.
    probe.style.position = 'fixed';
    probe.style.top = '0';
    probe.style.left = '-99999px';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';
    probe.setAttribute('aria-hidden', 'true');
    document.body.append(probe);
  }
  probe.className = className;
  // Use container width, not text width: probe classes apply indentation and
  // maximum width exactly as during rendering.
  probe.style.width = `${width}px`;
  return probe;
}

/**
 * Measures `text` rendered with `className` in a `width`-wide container and
 * returns its line count.
 *
 * `lineHeight` is the spacing fixed by those classes: it converts measured height
 * into lines and is the contract reserved by the caller.
 *
 * Returns `0` for empty text and `1` outside a browser — tests and server
 * rendering have no DOM to query, and one line is the closest case.
 */
export function measureLines(
  text: string,
  width: number,
  className: string,
  lineHeight: number,
): number {
  if (!text) return 0;
  if (typeof document === 'undefined' || width <= 0) return 1;

  if (width !== cacheWidth) {
    cache.clear();
    cacheWidth = width;
  }
  const key = `${className} ${text}`;
  const known = cache.get(key);
  if (known !== undefined) return known;

  const element = probeElement(className, width);
  element.textContent = text;
  const lines = Math.max(1, Math.round(element.offsetHeight / lineHeight));
  cache.set(key, lines);
  return lines;
}
